'use client';

import { useRouter } from 'next/router';
import { PropsWithChildren } from 'react';
import { FallbackProps, ErrorBoundary } from 'react-error-boundary';
import { classnames } from 'tailwindcss-classnames';

type DefaultErrorBoundaryProps = PropsWithChildren<{}>;

export { ErrorBoundary as ReactErrorBoundary };

/**
 * Fallback component is shown when an error occurs.
 * @param {FallbackProps} param0 Component properties
 * @returns {React.JSX.Element} The component displayed to the user.
 */
export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <>
      <div
        className={classnames(
          'absolute',
          'left-1/2',
          'top-1/3',
          'z-[9999]',
          '-translate-x-1/2',
          '-translate-y-1/2'
        )}
      >
        <div
          className={classnames(
            'grid',
            'grid-flow-col',
            'grid-rows-2',
            'justify-items-center',
            'rounded-lg',
            'border-0',
            'bg-white',
            'p-7',
            'shadow-lg',
            'outline-none',
            'focus:outline-none'
          )}
        >
          <div>
            <h2>Error Occurred</h2>
            <p>{error.message}</p>
          </div>
          <div className={classnames('py-2')}>
            <button type="button" onClick={resetErrorBoundary}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * A simple error boundary that shows a popup and lets users reload the page.
 * @param param0 Component Properties
 * @returns {ErrorBoundary} Component
 */
const ErrorBoundaryDynatrace = ({ children, ...props }: DefaultErrorBoundaryProps) => {
  const { reload } = useRouter();
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} {...props} onReset={reload}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryDynatrace;
